import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import type { AccessibilityFix, AccessibilityIssue } from '../utils/accessibilityChecker';

export interface AccessibilityPanelProps {
  issues: AccessibilityIssue[];
  /** Apply a one-click fix. Wired in the editor page to slideElements.updateElement. */
  onApplyFix?: (issue: AccessibilityIssue, fix: AccessibilityFix) => void;
  /** Optional: select the element on the canvas when the user clicks the row. */
  onSelectElement?: (elementId: string) => void;
}

const AccessibilityPanel: React.FC<AccessibilityPanelProps> = ({
  issues,
  onApplyFix,
  onSelectElement,
}) => {
  if (issues.length === 0) {
    return (
      <Box sx={{ p: 2, minWidth: 320 }}>
        <Typography variant="body2" color="text.secondary">
          No accessibility issues detected on this slide.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, minWidth: 360, maxWidth: 440 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        Accessibility issues on this slide
      </Typography>
      <Stack spacing={1.5}>
        {issues.map((issue) => (
          <Box
            key={issue.id}
            sx={{
              borderLeft: 3,
              borderColor: issue.severity === 'fail' ? 'error.main' : 'warning.main',
              pl: 1.5,
              py: 0.5,
              cursor: onSelectElement ? 'pointer' : 'default',
              '&:hover': onSelectElement ? { backgroundColor: 'action.hover' } : undefined,
            }}
            onClick={() => onSelectElement?.(issue.elementId)}
          >
            <Stack direction="row" spacing={0.75} alignItems="flex-start">
              {issue.severity === 'fail' ? (
                <ErrorOutlineIcon fontSize="small" color="error" sx={{ mt: '2px' }} />
              ) : (
                <WarningAmberIcon fontSize="small" color="warning" sx={{ mt: '2px' }} />
              )}
              <Typography variant="body2" sx={{ flex: 1 }}>
                {issue.message}
              </Typography>
            </Stack>
            {issue.fixes.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
                {issue.fixes.map((fix, idx) => (
                  <Button
                    key={`${issue.id}-fix-${idx}`}
                    size="small"
                    variant="outlined"
                    onClick={(e) => {
                      e.stopPropagation();
                      onApplyFix?.(issue, fix);
                    }}
                    disabled={!onApplyFix}
                  >
                    {fix.label}
                  </Button>
                ))}
              </Stack>
            )}
          </Box>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        Contrast checks only — other accessibility requirements may still apply.
      </Typography>
    </Box>
  );
};

export default AccessibilityPanel;
